'use client';

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '#/components/ui/dialog';
import { EthereumStakingValidatorInfo } from '#/data/react-query/queries/order-list';
import { useWriteDirectStakingStakeCompound } from '#/generated';
import { DialogContentProps, DialogProps } from '@radix-ui/react-dialog';
import { animated, useSpringRef, useTransition } from '@react-spring/web';
import * as Sentry from '@sentry/nextjs';
import { useActor, useInterpret } from '@xstate/react';
import { Translation } from 'intl-lib';
import invariant from 'invariant';
import { useEffect, useMemo, useRef } from 'react';
import { usePrevious } from 'react-use';
import { toast } from 'sonner';
import { parseEther } from 'viem';
import { Steps } from './components/steps';
import machine, { MachineContext } from './contexts/machine';
import { Information } from './information';
import { TransactionReceipt } from './transaction-receipt';

interface Props {
  originalRow: EthereumStakingValidatorInfo;
}
function DialogContentImpl({ originalRow }: Props) {
  const { writeContractAsync: stakeCompoundAsync } =
    useWriteDirectStakingStakeCompound();

  const interpreter = useInterpret(machine, {
    context: originalRow
      ? {
          target: originalRow,
        }
      : undefined,
    services: {
      onTransaction: async (context, event) => {
        console.log('====================================');
        console.log('onTransaction', context);
        console.log('====================================');
        const { target, amount } = context;
        console.log('====================================');
        console.log('onTransaction', {
          target,
          amount,
        });
        console.log('====================================');
        invariant(target != null, 'target is required');
        invariant(amount != null, 'amount is required');

        try {
          const amountWei = parseEther(String(amount));
          // fixme top-up
          const transactionHash = await stakeCompoundAsync({
            args: [] as any,
            // @ts-ignore
            value,
          });

          console.log('====================================');
          console.log('Transaction Success', transactionHash);
          console.log('====================================');

          return { transactionHash };
        } catch (error) {
          console.log('====================================');
          console.log(error);
          console.log('====================================');
          Sentry.captureException(error);
          // @ts-ignore
          const transaction = error.transaction;
          if (transaction) {
            toast('Transaction Error', {
              description: (error as any).reason,
            });
          } else {
            toast('Error', {
              description: (error as unknown as Error).message,
            });
          }
          throw error;
        }
      },
    },
  });
  const [state, dispatch] = useActor(interpreter);

  const stepIndices = useMemo(() => ['information', 'transactionReceipt'], []);
  const stepIndex = useMemo(
    () =>
      stepIndices.findIndex(parentStateValue =>
        state.matches(parentStateValue)
      ),
    [state, stepIndices]
  );

  const stepComponents = useMemo(() => [Information, TransactionReceipt], []);

  const stepIndexPrevious = usePrevious(stepIndex);
  const isToLeft = stepIndexPrevious != null && stepIndex > stepIndexPrevious;

  const springRef = useSpringRef();

  const immediateRef = useRef(true);

  const transition = useTransition(stepIndex, {
    ref: springRef,
    keys: null,
    from: {
      opacity: 0,
      transform: isToLeft ? 'translate3d(10%,0,0)' : 'translate3d(-10%,0,0)',
      height: 0,
    },
    enter: {
      opacity: 1,
      transform: 'translate3d(0%,0,0)',
      height: 'auto',
    },
    leave: {
      opacity: 0,
      transform: isToLeft ? 'translate3d(-5%,0,0)' : 'translate3d(5%,0,0)',
      height: 0,
    },
    immediate: immediateRef.current,
  });

  useEffect(() => {
    springRef.start();

    if (immediateRef.current) {
      immediateRef.current = false;
    }
  }, [springRef, stepIndex]);

  return (
    <MachineContext.Provider value={interpreter}>
      <DialogHeader>
        <DialogTitle>
          <Translation path="my_staking.TopUp.Title" />
        </DialogTitle>
        <DialogDescription asChild>
          <Steps selectedIndex={stepIndex} className="py-6" />
        </DialogDescription>
      </DialogHeader>
      {transition((style, stepIndex) => {
        const StepComponent = stepComponents[stepIndex];
        return (
          <animated.div style={style} className="flex flex-col gap-4">
            <StepComponent />
          </animated.div>
        );
      })}
    </MachineContext.Provider>
  );
}

export function TopUpDialogContent({
  originalRow,
  ...props
}: Props & DialogContentProps) {
  return (
    <DialogContent
      {...props}
      className="sm:max-w-(--breakpoint-sm) overflow-y-scroll max-h-screen"
    >
      <DialogContentImpl originalRow={originalRow} />
    </DialogContent>
  );
}

export function TopUpDialog({ originalRow, ...props }: Props & DialogProps) {
  return (
    <Dialog {...props}>
      <TopUpDialogContent originalRow={originalRow} />
    </Dialog>
  );
}
